// Editable · Switch
//
// Wraps AntD <Switch> with the DS token theme so boolean toggles render
// consistently across the editable tier. Exposes a clean Input/Output
// contract and forwards `data-testid` onto the AntD Switch button itself
// (not a wrapper span) so e2e specs can locate + click the toggle directly.
//
// AntD wrap justification (Rule 3 — non-trivial I/O surface + Form.Item
// binding): the canonical way to render a boolean field is a Switch, and
// AntD's Switch binds inside `Form.Item` via `valuePropName="checked"`. The
// wrapper is deliberately transparent to that binding — it spreads `checked`
// + `onChange` straight through so a `<Form.Item valuePropName="checked">`
// parent injects them exactly as AntD expects. Mirrors the `boolean` case in
// `fieldTypeRegistry.tsx`.

import type React from "react";
import { Switch as AntSwitch } from "antd";

// ─── Inputs (data props — the model) ─────────────────────────────────────────

export interface SwitchInput {
  /**
   * Controlled checked state. Optional so AntD `Form.Item` (with
   * `valuePropName="checked"`) can inject it at runtime.
   */
  checked?: boolean;
  /** Disable the toggle. Default false. */
  disabled?: boolean;
  /** AntD size. Default "default". */
  size?: "default" | "small";
  /** Auto-focus on mount — for inline-edit affordances. */
  autoFocus?: boolean;
  /**
   * `data-testid` on the AntD Switch button element (reachable + clickable),
   * so `getByTestId(x).click()` toggles the switch.
   */
  testId?: string;
}

// ─── Outputs (events emitted — controller hooks) ──────────────────────────────

export interface SwitchOutput {
  /** Fires with the new checked state on toggle. */
  onChange?: (checked: boolean) => void;
}

export type SwitchProps = SwitchInput & SwitchOutput;

// ─── Component ────────────────────────────────────────────────────────────────

export function Switch({
  checked,
  disabled = false,
  size = "default",
  autoFocus,
  testId,
  onChange,
}: SwitchProps) {
  return (
    <AntSwitch
      checked={checked}
      disabled={disabled}
      size={size}
      autoFocus={autoFocus}
      onChange={(next: boolean) => onChange?.(next)}
      // `data-testid` flows through AntD's rest-props onto the underlying
      // <button role="switch"> — reachable + clickable for e2e.
      {...(testId ? { "data-testid": testId } : {})}
    />
  );
}
